(() => {
  const key = 'jp-ecol-quote-list:v1';
  const supports = new Set(['プラスチックタイプ', '亜鉛メッキ支柱']);
  // 商品ページの入力欄（properties[...]）を、お問い合わせへ引き継ぐ。自由文に個人の情報が入り得るため URL には載せない
  const handoffKey = 'jp-ecol-contact-handoff:v1';
  const oneLine = (value, max) => Array.from(String(value ?? '').replace(/\s+/g, ' ').trim()).slice(0, max).join('');
  const normalizeProperties = (value) => {
    if (value === undefined || value === null) return [];
    if (!Array.isArray(value) || value.length > 10) throw new Error('invalid-properties');
    return value.map((property) => {
      const name = oneLine(property?.name, 50);
      if (!name || name.startsWith('_')) throw new Error('invalid-properties');
      // 値が空のものは「必須なのに未入力」の印（任意の欄が空なら、そもそも入れない）
      return { name, value: oneLine(property?.value, 500) };
    });
  };
  // 送る時と同じ選び方：使える欄だけ、ラジオは選ばれたもの。必須の欄が空なら「未入力」として残す
  const collectProperties = (root) => {
    const form = root?.querySelector('form.jp-ecol-product-main__form');
    if (!form) return [];
    const properties = [];
    Array.from(form.elements).forEach((field) => {
      const match = /^properties\[(.+)\]$/.exec(field.name || '');
      if (!match || field.disabled || match[1].startsWith('_')) return;
      if ((field.type === 'radio' || field.type === 'checkbox') && !field.checked) return;
      if (properties.some((property) => property.name === match[1])) return;
      const value = oneLine(field.value, 500);
      if (value || field.required) properties.push({ name: match[1], value });
    });
    return normalizeProperties(properties.slice(0, 10));
  };
  const normalize = (value) => {
    const url = new URL(value.product_url, location.origin);
    const id = String(value.variant_id || '');
    const quantity = Number(value.quantity);
    if (url.origin !== location.origin || url.protocol !== 'https:'
      || !/^\/products\/[^/]+$/.test(url.pathname) || !/^\d+$/.test(id)
      || !Number.isInteger(quantity) || quantity < 1 || quantity > 9999) throw new Error('invalid-item');
    url.search = '';
    url.hash = '';
    const support = url.pathname === '/products/sheet-pond-round-tank' && supports.has(value.support) ? value.support : '';
    return { product_url: url.href, variant_id: id, quantity, support, properties: normalizeProperties(value.properties) };
  };
  // 同じ商品・仕様でも、入力した内容（ご用途など）が違えば別の行にする
  const itemKey = (item) => `${item.variant_id}:${item.support || ''}:${JSON.stringify((item.properties || []).map((property) => [property.name, property.value]))}`;
  const read = () => {
    const items = JSON.parse(sessionStorage.getItem(key) || '[]');
    if (!Array.isArray(items) || items.length > 20) throw new Error('invalid-list');
    return items.map(normalize);
  };
  const isQuoteProduct = (root) => Boolean(root?.hasAttribute('data-jp-ecol-quote-product'));
  const singleLabelFor = (root) => (isQuoteProduct(root) ? '見積もり・購入の相談に進む' : '購入前の案内・相談に進む');
  const addLabelFor = (root) => (isQuoteProduct(root) ? 'まとめて見積もりするリストに追加' : 'まとめて問い合わせるリストに追加');
  const saveErrorFor = (root) => `${addLabelFor(root).replace(/に追加$/, '')}に保存できませんでした。「${singleLabelFor(root)}」からお問い合わせください。`;
  const multipleLabel = (count) => `${count}商品まとめて見積もり・相談に進む`;
  // 文字が同じなら書き換えない。押している途中のリンクの文字を差し替えると、WebKit ではそのタップが届かなくなる
  const setText = (node, text) => { if (node && node.textContent !== text) node.textContent = text; };
  const syncProduct = (root, options = {}) => {
    const primary = root.querySelector('[data-jp-ecol-consult-cta]');
    const secondary = root.querySelector('[data-jp-ecol-secondary-consult]');
    const button = root.querySelector('[data-jp-ecol-quote-add]');
    const quantity = root.querySelector('input[name="quantity"]');
    if (!primary?.dataset.singleInquiryUrl) return;
    const url = new URL(primary.dataset.singleInquiryUrl, location.origin);
    const selectedKey = itemKey({ ...Object.fromEntries(url.searchParams), properties: collectProperties(root) });
    let items = [];
    try { items = read(); } catch (_error) { /* Keep the single-item inquiry available. */ }
    const selected = items.find((item) => itemKey(item) === selectedKey);
    if (root.dataset.quoteSelectedKey !== selectedKey) {
      const status = root.querySelector('[data-jp-ecol-quote-status]');
      if (status) status.hidden = true;
    }
    // Restore saved quantities only on selection/history changes, never while typing.
    if (selected && quantity && (root.dataset.quoteSelectedKey !== selectedKey || options.restoreQuantity)) {
      quantity.value = String(selected.quantity);
      url.searchParams.set('quantity', quantity.value);
      primary.dataset.singleInquiryUrl = url.href;
    }
    root.dataset.quoteSelectedKey = selectedKey;
    const multiple = selected && items.length > 1;
    const label = multiple ? multipleLabel(items.length) : singleLabelFor(root);
    setText(primary, label);
    primary.href = multiple ? '/pages/contact?quote=list' : url.href;
    if (secondary) {
      const advice = new URL(url.href);
      advice.searchParams.set('intent', 'advice');
      setText(secondary, label);
      secondary.href = multiple ? '/pages/contact?quote=list' : advice.href;
      secondary.hidden = !primary.hidden;
    }
    if (button) {
      button.disabled = Boolean(selected);
      const label = button.querySelector?.('[data-jp-ecol-quote-add-label]') || button;
      setText(label, selected ? 'お問い合わせリストに追加済み' : addLabelFor(root));
    }
    const next = root.querySelector('[data-jp-ecol-quote-next]');
    if (next) next.hidden = !selected;
  };
  const refresh = () => {
    let count = 0;
    try { count = read().length; } catch (_error) { /* Direct inquiry remains available. */ }
    document.querySelectorAll('[data-jp-ecol-quote-count]').forEach((node) => { node.textContent = String(count); });
    document.querySelectorAll('[data-jp-ecol-quote-open]').forEach((node) => {
      node.textContent = count > 1 ? multipleLabel(count) : 'リストに追加した商品の見積もり・相談に進む';
    });
    document.querySelectorAll('[data-jp-ecol-quote-bar], [data-jp-ecol-quote-review-link]').forEach((node) => { node.hidden = count === 0; });
    document.querySelectorAll('.jp-ecol-product-main').forEach((root) => syncProduct(root));
  };
  const write = (items) => {
    const clean = items.map(normalize);
    if (clean.length > 20) throw new Error('list-full');
    sessionStorage.setItem(key, JSON.stringify(clean));
    refresh();
  };
  const add = (value) => {
    const next = normalize(value);
    const items = read();
    const existing = items.find((item) => itemKey(item) === itemKey(next));
    if (existing && existing.quantity + next.quantity > 9999) throw new Error('quantity-limit');
    if (existing) existing.quantity += next.quantity;
    else items.push(next);
    write(items);
  };
  const saveHandoff = (root, link) => {
    try {
      const primary = root.querySelector('[data-jp-ecol-consult-cta]');
      const variantId = new URL(primary?.dataset.singleInquiryUrl || link.href, location.origin).searchParams.get('variant_id') || '';
      const properties = collectProperties(root);
      if (!/^\d+$/.test(variantId) || !properties.length) {
        sessionStorage.removeItem(handoffKey);
        return;
      }
      sessionStorage.setItem(handoffKey, JSON.stringify({ variant_id: variantId, properties }));
    } catch (_error) { /* The inquiry still opens without the extra fields. */ }
  };
  const readHandoff = (variantId) => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(handoffKey) || 'null');
      if (!saved || String(saved.variant_id) !== String(variantId)) return [];
      return normalizeProperties(saved.properties);
    } catch (_error) {
      return [];
    }
  };
  const clearHandoff = () => {
    try { sessionStorage.removeItem(handoffKey); } catch (_error) { /* Nothing to clear. */ }
  };
  window.JpEcolQuoteList = { read, write, itemKey, syncProduct, readHandoff, clearHandoff };

  // 「購入前の案内・相談に進む」「見積もり・購入の相談に進む」を押した時点の入力を、お問い合わせ側へ渡す
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-jp-ecol-consult-cta], [data-jp-ecol-secondary-consult]');
    const root = link?.closest('.jp-ecol-product-main');
    if (root) saveHandoff(root, link);
  }, true);
  // 入力を変えたら、リストに入っている行かどうかの表示を合わせ直す
  document.addEventListener('input', (event) => {
    if (!/^properties\[/.test(event.target.name || '')) return;
    const root = event.target.closest('.jp-ecol-product-main');
    // ご用途の切り替えで型番欄が出入りした後（change の処理の後）に合わせる
    if (root) setTimeout(() => syncProduct(root), 0);
  });

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-jp-ecol-quote-add]');
    if (!button || button.disabled) return;
    const root = button.closest('.jp-ecol-product-main');
    const link = root?.querySelector('[data-jp-ecol-consult-cta]');
    const status = root?.querySelector('[data-jp-ecol-quote-status]');
    const quantity = root?.querySelector('input[name="quantity"]');
    const support = root?.querySelector('[data-jp-ecol-support-select]');
    if (!link || !status || (quantity && !quantity.reportValidity()) || (support && !support.reportValidity())) return;
    try {
      const params = new URL(link.dataset.singleInquiryUrl || link.href, location.origin).searchParams;
      add({ ...Object.fromEntries(params), properties: collectProperties(root) });
      status.textContent = 'お問い合わせリストに追加しました。';
      status.dataset.state = 'success';
    } catch (error) {
      status.textContent = error.message === 'list-full'
        ? '一度にお問い合わせできるのは20種類までです。お問い合わせリストをご確認ください。'
        : error.message === 'quantity-limit'
        ? '同じ仕様は9999点までお問い合わせできます。お問い合わせリストの数量をご確認ください。'
        : saveErrorFor(root);
      status.dataset.state = 'error';
    }
    status.hidden = false;
  });
  document.addEventListener('input', (event) => {
    if (!event.target.matches('input[name="quantity"]')) return;
    const root = event.target.closest('.jp-ecol-product-main');
    const link = root?.querySelector('[data-jp-ecol-consult-cta]');
    if (!link?.dataset.singleInquiryUrl || !event.target.checkValidity()) return;
    try {
      const items = read();
      const selected = items.find((item) => itemKey(item) === root.dataset.quoteSelectedKey);
      if (!selected) return;
      selected.quantity = Number(event.target.value);
      write(items);
    } catch (_error) {
      const status = root.querySelector('[data-jp-ecol-quote-status]');
      status.textContent = saveErrorFor(root);
      status.dataset.state = 'error';
      status.hidden = false;
      [link, root.querySelector('[data-jp-ecol-secondary-consult]')].filter(Boolean).forEach((node) => {
        node.href = link.dataset.singleInquiryUrl;
        node.textContent = singleLabelFor(root);
      });
    }
  });
  refresh();
})();
